// src/pages/TaxQuotePage/SummaryPage.tsx

import React, { useContext } from "react";
import { QuoteContext } from "../../context/QuoteContext";
import { useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { FaClipboardList } from "react-icons/fa";
import styles from "./Quote.module.scss";

const SummaryPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { basic, investment, foreignIncome, otherMisc, resetAll } =
    useContext(QuoteContext)!;

  const filterItems = (categories: any[]) =>
    categories
      .map((category) => ({
        ...category,
        services: category.services.filter((s: any) => s.unit > 0),
      }))
      .filter((category) => category.services.length > 0);

  const filteredBasic = filterItems(basic);
  const filteredInvestment = filterItems(investment);
  const filteredForeignIncome = filterItems(foreignIncome);
  const filteredOtherMisc = filterItems(otherMisc);

  const calculateTotal = () => {
    const sumItems = (cats: any[]) =>
      cats.reduce(
        (acc, category) =>
          acc +
          category.services.reduce(
            (sum: number, service: any) => sum + service.total,
            0
          ),
        0
      );

    return (
      sumItems(filteredBasic) +
      sumItems(filteredInvestment) +
      sumItems(filteredForeignIncome) +
      sumItems(filteredOtherMisc)
    );
  };

  const handleBack = () => {
    navigate("/quote/other_misc");
  };

  // 将提交功能改为重置功能：点击后清空并返回Basic页面
  const handleReset = () => {
    resetAll();
    navigate("/quote/basic");
  };

  const renderTable = (title: string, categories: any[]) => {
    if (categories.length === 0) return null;

    return (
      <div className={styles.summarySection}>
        <h3>{title}</h3>
        <div className={styles.tableContainer}>
          <table>
            <thead>
              <tr>
                <th>{t("taxQuote.service")}</th>
                <th>{t("taxQuote.unitPrice")}</th>
                <th>{t("taxQuote.unit")}</th>
                <th>{t("taxQuote.total")}</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((category: any, cIndex: number) =>
                category.services.map((service: any, sIndex: number) => (
                  <tr key={service.id}>
                    <td className={styles.serviceName}>
                      {t(service.nameKey)}
                      {service.noteKey && (
                        <span className={styles.small_note}>
                          {t(service.noteKey)}
                        </span>
                      )}
                    </td>
                    <td>${service.unitPrice.toFixed(2)}</td>
                    <td>{service.unit}</td>
                    <td>${service.total.toFixed(2)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.summaryContainer}>
      <div className={styles.quoteBox}>
        <h2 className={styles.sectionTitle}>
          <FaClipboardList className={styles.titleIcon} />
          {t("taxQuote.summary")}
        </h2>

        {renderTable(t("taxQuote.basicPackages.serviceName"), filteredBasic)}
        {renderTable(t("taxQuote.investment.serviceName"), filteredInvestment)}
        {renderTable(
          t("taxQuote.foreignIncomeAndAssets.serviceName"),
          filteredForeignIncome
        )}
        {renderTable(t("taxQuote.otherMISC.serviceName"), filteredOtherMisc)}

        <div className={styles.overallTotal}>
          <strong>{t("taxQuote.grandTotal")}: </strong>$
          {calculateTotal().toFixed(2)}
        </div>

        <hr className={styles.divider} />
        {/* 新增的推荐文本：使用 Trans 组件多语言处理 */}
        <p className="referral-text">
          <Trans
            i18nKey="taxQuote.referralText"
            values={{ email: t("nav.email") }} // 使用键值对形式传入email值
            components={{
              strong: <strong />,
              a: <a href={`mailto:${t("nav.email")}`}>{t("nav.email")}</a>,
              br: <br />,
              em: <em />,
            }}
          />
        </p>

        <p className="referral-text">
          <strong>{t("taxQuote.deadlineText")}</strong>
        </p>

        <div className={styles.buttons}>
          <button onClick={handleBack}>{t("taxQuote.back")}</button>
          <button onClick={handleReset}>{t("taxQuote.reset")}</button>
        </div>
      </div>
    </div>
  );
};

export default SummaryPage;
